import React, { useRef, useEffect } from 'react';

export interface ITiltPoint {
  tiltX: number;
  tiltY: number;
}

// Tilt Canvas Component
const TiltCanvas: React.FC<{ tilts: ITiltPoint[] }> = React.memo(({ tilts }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const points: ITiltPoint[] = tilts ?? [];
    const size = canvas.width;
    const logicalMin = -2.5;
    const logicalMax = 2.5;
    const logicalRange = logicalMax - logicalMin;
    const scale = size / logicalRange;

    const toCanvas = (x: number, y: number) => ({
      x: (x - logicalMin) * scale,
      y: size - (y - logicalMin) * scale,
    });

    ctx.clearRect(0, 0, size, size);

    // Red background
    ctx.fillStyle = 'red';
    ctx.fillRect(0, 0, size, size);

    // Yellow and green circles
    const center = toCanvas(0, 0);
    ctx.fillStyle = 'yellow';
    ctx.beginPath();
    ctx.arc(center.x, center.y, 2 * scale, 0, 2 * Math.PI);
    ctx.fill();

    ctx.fillStyle = 'green';
    ctx.beginPath();
    ctx.arc(center.x, center.y, 1 * scale, 0, 2 * Math.PI);
    ctx.fill();

    // Draw major tick labels only
    ctx.fillStyle = 'black';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let i = -2; i <= 2; i++) {
      if (i !== 0) {
        ctx.fillText(i.toString(), toCanvas(i, 0).x, center.y + 10); // X labels
        ctx.fillText(i.toString(), center.x + 15, toCanvas(0, i).y); // Y labels
      }
    }

    // Axes
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, center.y);
    ctx.lineTo(size, center.y);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(center.x, 0);
    ctx.lineTo(center.x, size);
    ctx.stroke();

    // Draw tilt path
    const scaled = points.map(p => toCanvas(p.tiltX, p.tiltY));

    ctx.strokeStyle = 'black';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    scaled.forEach((pt, i) => {
      i === 0 ? ctx.moveTo(pt.x, pt.y) : ctx.lineTo(pt.x, pt.y);
    });
    ctx.stroke();

    // Black dots
    ctx.fillStyle = 'black';
    scaled.slice(0, -1).forEach(pt => {
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 3, 0, 2 * Math.PI);
      ctx.fill();
    });

    // Last point as blue cross
    if (scaled.length > 0) {
      const pt = scaled[scaled.length - 1];
      ctx.strokeStyle = 'blue';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(pt.x - 6, pt.y - 6);
      ctx.lineTo(pt.x + 6, pt.y + 6);
      ctx.moveTo(pt.x + 6, pt.y - 6);
      ctx.lineTo(pt.x - 6, pt.y + 6);
      ctx.stroke();
    }
  }, [tilts]);

  return (
    <canvas
      ref={canvasRef}
      width={400}
      height={400}
      style={{ border: '1px solid #ccc' }}
    />
  );
});

export default TiltCanvas;
